import { randomColorSwatch } from "@/constants/color-swatches";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { RecurrenceConfig } from "@/components/events/recurrence/rrule";
import { toZonedISOString, wallClockInZone, type TWallClock } from "@/lib/zoned-time";

const STORAGE_KEY = "event-draft";

/** The browser's IANA timezone, falling back to UTC when unavailable. */
function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (error) {
    console.warn("Failed to resolve browser timezone; defaulting to UTC", error);
    return "UTC";
  }
}

/** A draft persisted before the schedule became a wall clock (NG-1791). */
type TLegacyDraftData = TDraftData & {
  startDatetime?: string | null;
  endDatetime?: string | null;
};

type TDraftData = {
  title: string;
  description: string;
  durationMinutes: number;
  assigneeIds: string[];
  skillIds: string[];
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  loadedStartDatetime: string | null;
  loadedEndDatetime: string | null;
  projectId: string | null;
  homeTeamMode?: TEventHomeTeamMode | null;
  homeTeamId?: string | null;
  timezone: string;
  categoryValueId: string | null;
  stateId: string | null;
  color: string;
};

/** How a new event gets its Home team (NG-2155, FR33): through a linked project, or
 *  set directly. `null` until the author picks one of the two cards. */
export type TEventHomeTeamMode = "project" | "team";

/** FR33: Create waits for a picked card and that card's value. The one rule for
 *  every event-create surface — the full page (via this store) and the Create
 *  Work Item dialog (`useCreateEventHomeTeam`) — so the two cannot drift. */
export function isHomeTeamComplete(
  mode: TEventHomeTeamMode | null,
  projectId: string | null,
  homeTeamId: string | null,
): boolean {
  if (mode === "project") return Boolean(projectId);
  if (mode === "team") return Boolean(homeTeamId);
  return false;
}

export class EventDraftStore {
  title = "";
  description = "";
  durationMinutes = 60;
  assigneeIds: string[] = [];
  skillIds: string[] = [];
  // The schedule is held as the wall clock the author picked, not as an instant
  // (NG-1791). Storing a rendered instant beside a separately settable timezone
  // is two fields that can disagree, which is the shape of the bug this ticket
  // exists to end: changing the zone left the instant standing and silently
  // changed what the event meant. Held this way the two cannot disagree, because
  // the instant is derived from them — see `startDatetime` below.
  startDate: string | null = null;
  startTime: string | null = null;
  endDate: string | null = null;
  endTime: string | null = null;
  /**
   * The instants this draft was *loaded* with, if it was loaded from a saved
   * event rather than picked by hand.
   *
   * A wall clock does not always name one instant. On the hour a zone repeats
   * at the end of summer time, 01:30 in Chicago happens twice, and resolving
   * that clock has to choose — these getters take the first occurrence. So a
   * draft loaded at the *second* 01:30 would hand back the first, quietly
   * moving the event an hour. Keeping what we were given means an untouched
   * draft returns exactly that, and only an actual edit re-derives.
   */
  loadedStartDatetime: string | null = null;
  loadedEndDatetime: string | null = null;
  // Optional project for the event. `null` = a workspace event with no project.
  projectId: string | null = null;
  // Home team (NG-2155), read only while the `home-team` flag is on. The mode is
  // which card is picked; `homeTeamId` is the direct Home of a project-less event.
  // Written together by `setHomeTeam`, so a draft is never in both modes.
  homeTeamMode: TEventHomeTeamMode | null = null;
  homeTeamId: string | null = null;
  // IANA timezone the event's wall-clock times are defined in. Defaults to the
  // browser tz; the create UI overrides it with the user's configured tz once
  // known. Persisted with the draft so a refresh keeps the chosen zone.
  timezone: string = browserTimezone();
  // Managed Event Category value chosen for the draft (null = none yet).
  categoryValueId: string | null = null;
  // Work item status for the draft. `null` = let the backend apply the
  // workspace's default (#3754).
  stateId: string | null = null;
  // The event's colour (NG-1594). Rolled when the draft is created, so the
  // picker always shows the colour the event will actually have rather than an
  // empty control — and the author can change it before saving.
  color: string = randomColorSwatch();
  // Recurrence for the draft (NG-395). `null` = a one-off event (the default);
  // set by the recurrence editor to make the event repeat.
  recurrence: RecurrenceConfig | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.restoreFromStorage();
    reaction(
      () => this.serialize(),
      (data) => {
        try {
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (err) {
          // sessionStorage may be unavailable
          console.warn("[event-draft] persist to sessionStorage failed", err);
        }
      },
    );
  }

  private serialize(): TDraftData {
    return {
      title: this.title,
      description: this.description,
      durationMinutes: this.durationMinutes,
      assigneeIds: [...this.assigneeIds],
      skillIds: [...this.skillIds],
      loadedStartDatetime: this.loadedStartDatetime,
      loadedEndDatetime: this.loadedEndDatetime,
      startDate: this.startDate,
      startTime: this.startTime,
      endDate: this.endDate,
      endTime: this.endTime,
      projectId: this.projectId,
      homeTeamMode: this.homeTeamMode,
      homeTeamId: this.homeTeamId,
      timezone: this.timezone,
      categoryValueId: this.categoryValueId,
      stateId: this.stateId,
      color: this.color,
    };
  }

  private restoreFromStorage(): void {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as TLegacyDraftData;
      runInAction(() => {
        if (data.title) this.title = data.title;
        if (data.description) this.description = data.description;
        if (data.durationMinutes) this.durationMinutes = data.durationMinutes;
        if (data.assigneeIds?.length) this.assigneeIds = data.assigneeIds;
        if (data.skillIds?.length) this.skillIds = data.skillIds;
        if (data.projectId) this.projectId = data.projectId;
        if (data.homeTeamMode) this.homeTeamMode = data.homeTeamMode;
        if (data.homeTeamId) this.homeTeamId = data.homeTeamId;
        // Before the wall clock, so a legacy payload is read back against the
        // zone it was written in rather than the default.
        if (data.timezone) this.timezone = data.timezone;
        if (data.startDate && data.startTime && data.endDate && data.endTime) {
          this.setScheduledWallClock(
            { date: data.startDate, time: data.startTime },
            { date: data.endDate, time: data.endTime },
          );
          // Restored, not re-picked: keep whatever instants the draft carried so
          // a repeated wall clock still resolves the way it did when loaded.
          this.loadedStartDatetime = data.loadedStartDatetime ?? null;
          this.loadedEndDatetime = data.loadedEndDatetime ?? null;
        } else if (data.startDatetime && data.endDatetime) {
          // A draft written before the schedule became a wall clock. Same
          // storage key, so without this the author loses the time they picked
          // on their next reload — silently, which is the worst way to lose it.
          this.loadScheduleFromInstants(data.startDatetime, data.endDatetime);
        }
        if (data.categoryValueId) this.categoryValueId = data.categoryValueId;
        if (data.stateId) this.stateId = data.stateId;
        if (data.color) this.color = data.color;
      });
    } catch (err) {
      // sessionStorage may be unavailable
      console.warn("[event-draft] restore from sessionStorage failed", err);
    }
  }

  /** True once the draft has a start and an end to save. */
  get isScheduled(): boolean {
    return Boolean(this.startDate && this.startTime && this.endDate && this.endTime);
  }

  /**
   * The draft's start as the offset-stamped instant the API stores — derived, so
   * it always agrees with the wall clock and the zone it was picked in. Changing
   * `timezone` therefore moves the instant and keeps the clock with no
   * restatement step to remember, which is the whole point of holding the wall
   * clock rather than a rendered string.
   */
  get startDatetime(): string | null {
    if (this.loadedStartDatetime) return this.loadedStartDatetime;
    if (!this.startDate || !this.startTime) return null;
    return toZonedISOString(this.startDate, this.startTime, this.timezone);
  }

  get endDatetime(): string | null {
    if (this.loadedEndDatetime) return this.loadedEndDatetime;
    if (!this.endDate || !this.endTime) return null;
    return toZonedISOString(this.endDate, this.endTime, this.timezone);
  }

  get isEmpty(): boolean {
    return this.title === "" && this.assigneeIds.length === 0;
  }

  setTitle(title: string): void {
    this.title = title;
  }

  setDescription(description: string): void {
    this.description = description;
  }

  setDurationMinutes(minutes: number): void {
    this.durationMinutes = minutes;
  }

  setAssigneeIds(ids: string[]): void {
    this.assigneeIds = ids;
  }

  setSkillIds(ids: string[]): void {
    this.skillIds = ids;
  }

  setProjectId(projectId: string | null): void {
    this.projectId = projectId;
  }

  /** A Home-team pick from the field (FR33), written whole: the field has already
   *  cleared the other card's value, so the draft is never in both modes. */
  setHomeTeam(pick: {
    mode: TEventHomeTeamMode;
    projectId: string | null;
    homeTeamId: string | null;
  }): void {
    this.homeTeamMode = pick.mode;
    this.projectId = pick.projectId;
    this.homeTeamId = pick.homeTeamId;
  }

  /** The picked card. A draft that predates the cards (restored from storage, or
   *  filled with the flag off) and already names a project reads as linked. */
  get effectiveHomeTeamMode(): TEventHomeTeamMode | null {
    return this.homeTeamMode ?? (this.projectId ? "project" : null);
  }

  /** The Home-team group is complete: a card is picked and its value chosen. */
  get homeTeamComplete(): boolean {
    return isHomeTeamComplete(this.effectiveHomeTeamMode, this.projectId, this.homeTeamId);
  }

  setTimezone(timezone: string): void {
    this.timezone = timezone;
    // Re-state the clock in the new zone: the instant must move, so a loaded
    // one is no longer what this draft means.
    this.forgetLoadedInstants();
  }

  setCategoryValueId(id: string | null): void {
    this.categoryValueId = id;
  }

  setStateId(id: string | null): void {
    this.stateId = id;
  }

  setColor(color: string): void {
    this.color = color;
  }

  setRecurrence(recurrence: RecurrenceConfig | null): void {
    this.recurrence = recurrence;
  }

  /** Read two instants back as the wall clock the draft's own zone shows. */
  private loadScheduleFromInstants(startDatetime: string, endDatetime: string): void {
    const start = wallClockInZone(startDatetime, this.timezone);
    const end = wallClockInZone(endDatetime, this.timezone);
    if (!start || !end) {
      this.clearScheduledTime();
      return;
    }
    this.setScheduledWallClock(start, end);
    this.loadedStartDatetime = startDatetime;
    this.loadedEndDatetime = endDatetime;
  }

  /** Set the schedule as the wall clock the author picked, in the draft's zone. */
  setScheduledWallClock(start: TWallClock, end: TWallClock): void {
    this.forgetLoadedInstants();
    this.startDate = start.date;
    this.startTime = start.time;
    this.endDate = end.date;
    this.endTime = end.time;
  }

  /** Drop the loaded instants, so the getters derive from the wall clock again. */
  private forgetLoadedInstants(): void {
    this.loadedStartDatetime = null;
    this.loadedEndDatetime = null;
  }

  clearScheduledTime(): void {
    this.forgetLoadedInstants();
    this.startDate = null;
    this.startTime = null;
    this.endDate = null;
    this.endTime = null;
  }

  reset(): void {
    this.title = "";
    this.description = "";
    this.durationMinutes = 60;
    this.assigneeIds = [];
    this.skillIds = [];
    this.clearScheduledTime();
    this.projectId = null;
    this.homeTeamMode = null;
    this.homeTeamId = null;
    this.timezone = browserTimezone();
    this.categoryValueId = null;
    this.stateId = null;
    this.color = randomColorSwatch();
    this.recurrence = null;
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // ignore
      console.warn("[event-draft] clear sessionStorage failed", err);
    }
  }

  loadFromEvent(data: {
    title: string;
    description?: string;
    durationMinutes: number;
    assigneeIds: string[];
    skillIds: string[];
    startDatetime: string | null;
    endDatetime: string | null;
    timezone?: string;
  }): void {
    this.title = data.title;
    this.description = data.description ?? "";
    this.durationMinutes = data.durationMinutes;
    this.assigneeIds = data.assigneeIds;
    this.skillIds = data.skillIds;
    // Set the zone first: the instants are read back as the clock THAT zone
    // shows, which is the clock the event is defined by.
    if (data.timezone) this.timezone = data.timezone;
    if (data.startDatetime && data.endDatetime) {
      this.loadScheduleFromInstants(data.startDatetime, data.endDatetime);
    } else {
      this.clearScheduledTime();
    }
  }
}
